import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from 'react-oidc-context';

// Define prop types
interface InspectionCameraProps {
  userName: string;
  inspectionId: string;
  backendUrl?: string;
  onUploadSuccess?: (response?: any) => void;
  onUploadError?: (error: Error) => void;
  requestId?: string;
  establishmentId?: string;
  documentCategory?: string;
  documentType?: string;
  notes?: string;
  ocrRequired?: string;
}

const InspectionCamera: React.FC<InspectionCameraProps> = ({
  userName,
  inspectionId,
  backendUrl = '',
  onUploadSuccess = () => {},
  onUploadError = () => {},
  requestId,
  establishmentId,
  documentCategory = 'establishment',
  documentType = 'emirates_id',
  notes = 'Inspection photo evidence',
  ocrRequired = 'false'
}) => {
  const auth = useAuth();
  const BASE_URL = 'https://services-sdp-dev.mohre.gov.ae';
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null); // Store the stream in a ref
  const [snapshot, setSnapshot] = useState<string | null>(null);
  const [file, setFile] = useState<File | null>(null);
  const [uploadStatus, setUploadStatus] = useState<string>('');
  const [locationInfo, setLocationInfo] = useState<string>('');
  const [latLong, setLatLong] = useState<string>('');
  const [isUploading, setIsUploading] = useState<boolean>(false);
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [currentCameraIndex, setCurrentCameraIndex] = useState<number>(0);

  // Canvas dimensions (A4 aspect ratio: 1240x1754)
  const canvasWidth = 1240;
  const canvasHeight = 1754;
  const stripHeight = 50; // Info strip height
  const videoHeight = canvasHeight - stripHeight; // 1704px for video
  const aspectRatio = canvasWidth / canvasHeight; // ~0.707

  // Load icons as base64 strings
  const icons: { [key: string]: HTMLImageElement } = {
    inspection: new Image(),
    user: new Image(),
    dateTime: new Image(),
    location: new Image(),
    latLong: new Image()
  };
  icons.inspection.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAfUlEQVR4nL2SywnAIBBEX19JG+kmNYRc86nARtJHbEPYsOBBjIpRyMC7LLODo0JZIzDQqBUQz1KzcAIuWFJmTzhTz5EKcJFRuT3xXL0viccCJoMNfNkAU6hpagKkkmzABUwJ/guQ3gqm9xJt6zO6D8dPfqS9MkQ9W6Hmz3oAAp13Ungre/4AAAAASUVORK5CYII=';
    icons.user.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAiklEQVR4nGNgGMygmoGB4RkUV5KqOYKBgeE/Gg4jxYA5WAwAiRENqrAYQJI3eBkYGE4iaT7BwMDAw0AiYGFgYLBmYGCwgrLpB7gZGBi8od6AARDbCyqHE7AzMDB0MDAwfIL6+woDA0MmFF+FioHk2qFqMcAmLCGPC2/AZsBfEgwAqcUAxGqG4UECAPnLQS5Ntx3VAAAAAElFTkSuQmCC';
    icons.dateTime.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAY0lEQVR4nGNgwAShDAwMMljEZaByBMF/HApDoXI4wRwGBoY/UEX4MEjNLGwG/CFCM7IhWJ3+nwSM1YBeqF/x4V58BoTiCyRCgfl/1AAGisPgDwnRiDUhgZInsUl5JhEupRMAAKccTSCqBnxTAAAAAElFTkSuQmCC';
    icons.location.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAACXBIWXMAAAsTAAALEwEAmpwYAAAAqklEQVR4nLWSMQrCQBBFX6tE1GtE0BNpo8kJNAcQ76CN2tl4JiWV2CpoqwxsYAjLOAn4YGAZ5r9lh4U/0QVy4BgqCz0XI6AEPrW6Aqnn5jIS1pKOJcjV8ANYAqtwrvoLS3BQgxKsKFR/bwl2DsHWEmS1JxShnqo/twRD4G0s8QUM+MHGEKxxkAC3SPgO9HAyiwimNOSkwuemYUGWJT/vAvTbCIQJMG4bdvEFELtKenhA4JsAAAAASUVORK5CYII=';
    icons.latLong.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQCAYAAAAf8/9hAAAACXBIWXMAAAsTAAALEwEAmpwYAAAA1klEQVR4nLWTzQ7BUBCFPyxY0ZWK8EIeQbdYYyVeAmvx0y4bPIrwBngRcmWGSTVlUSeZ9J45dyZzM6fwJ5TkOwZGiVwmPGADNIEBcJfoS24N1LKKz8AOKAI30+AKFIADcJK7HwiAGOgAbTnbaIsWy918MTXj1oGF4RpzwDfc1bwQGaGc4BqRaMq373KeRIUKEKY0CEVT7raV+gRfxk02mAENwyfkjeDLGltmjd20Bp6YZC+mcebRcS+Sc9oxy41OWIltnX21QU9yS6D6y3P0xxlK2Fy+eACFY0y1gJFjdgAAAABJRU5ErkJggg==';

  // Function to stop the webcam stream
  const stopStream = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(track => track.stop());
      streamRef.current = null;
    }
  };

  // Initialize webcam and canvas
  useEffect(() => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas?.getContext('2d');

    if (!video || !canvas || !context) return;

    // Get available cameras
    const getAvailableCameras = async () => {
      try {
        const devices = await navigator.mediaDevices.enumerateDevices();
        const videoDevices = devices.filter(device => device.kind === 'videoinput');
        setCameras(videoDevices);
        console.log('Available cameras:', videoDevices);
      } catch (err) {
        console.error('Error getting camera devices:', err);
      }
    };

    // Access webcam
    const startCamera = async (deviceId?: string) => {
      try {
        // Stop any existing stream
        stopStream();

        // Set video constraints
        const constraints = {
          video: deviceId ? { deviceId: { exact: deviceId } } : true
        };

        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        streamRef.current = stream; // Store the stream in ref
        if (video) {
          video.srcObject = stream;
          video.play();
        }
      } catch (err) {
        console.error('Error accessing webcam:', err);
        alert('Failed to access webcam: ' + err.message);
      }
    };

    // Get available cameras and start with default camera
    getAvailableCameras()
      .then(() => startCamera())
      .catch(err => {
        console.error('Error initializing camera:', err);
      });

    // Function to get location details from Google Maps API
    const getLocationFromGoogleAPI = async (latitude: number, longitude: number): Promise<string> => {
      try {
        // Check if Google Maps API is loaded
        if (window.google && window.google.maps && window.google.maps.Geocoder) {
          const geocoder = new window.google.maps.Geocoder();
          const latlng = { lat: latitude, lng: longitude };

          return new Promise((resolve, reject) => {
            geocoder.geocode({ location: latlng }, (results, status) => {
              if (status === 'OK' && results && results[0]) {
                resolve(results[0].formatted_address);
              } else {
                reject(new Error(`Geocoding failed with status: ${status}`));
              }
            });
          });
        } else {
          throw new Error('Google Maps API not loaded');
        }
      } catch (error) {
        console.error('Google Geocoding error:', error);
        throw error;
      }
    };

    // Get location and lat-long
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        position => {
          const { latitude, longitude } = position.coords;
          setLatLong(`${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
          console.log(latLong);
          // First try to get location from Google API
          getLocationFromGoogleAPI(latitude, longitude)
            .then(formattedAddress => {
              setLocationInfo(formattedAddress || 'Unknown');
            })
            .catch(err => {
              console.error('Google Geocoding error, falling back to OpenStreetMap:', err);

              // Fall back to OpenStreetMap if Google API fails
              fetch(`https://nominatim.openstreetmap.org/reverse?lat=${latitude}&lon=${longitude}&format=json`)
                .then(response => response.json())
                .then(data => {
                  setLocationInfo(data.display_name || 'Unknown');
                })
                .catch(osmErr => {
                  console.error('OpenStreetMap Geocoding error:', osmErr);
                  setLocationInfo('Location unavailable');
                });
            });
        },
        err => {
          console.error('Geolocation error:', err);
          setLocationInfo('Permission Denied');
        }
      );
    }

    // Draw live feed with info strip
    const drawLiveFeed = () => {
      if (!video || !context) return;

      // Clear canvas
      context.clearRect(0, 0, canvas.width, canvas.height);

      // Draw video frame (scale to fit portrait)
      const videoAspectRatio = video.videoWidth / video.videoHeight;
      const canvasAspectRatio = canvasWidth / videoHeight;
      let drawWidth = canvasWidth;
      let drawHeight = videoHeight;
      let offsetX = 0;
      let offsetY = 0;

      if (videoAspectRatio > canvasAspectRatio) {
        // Video is wider than canvas; fit by height
        drawWidth = videoHeight * videoAspectRatio;
        offsetX = (canvasWidth - drawWidth) / 2;
      } else {
        // Video is taller than canvas; fit by width
        drawHeight = canvasWidth / videoAspectRatio;
        offsetY = (videoHeight - drawHeight) / 2;
      }

      context.drawImage(video, offsetX, offsetY, drawWidth, drawHeight);

      // Draw strip background (light gray)
      context.fillStyle = 'lightgray';
      context.fillRect(0, videoHeight, canvasWidth, stripHeight);

      // Get current date and time in 12-hour format
      const now = new Date();
      const dateTime = now.toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).replace(',', '');

      // Set font style (small text, scaled for new dimensions)
      context.font = '18px Verdana, sans-serif';
      context.fillStyle = 'black';

      // Top Left: Inspection ID and User
      context.textAlign = 'left';
      context.drawImage(icons.inspection, 20, videoHeight + 5);
      context.fillText(`#${inspectionId}`, 50, videoHeight + 20);
      context.drawImage(icons.user, 500, videoHeight + 5);
      context.fillText(`${userName}`, 530, videoHeight + 20);

      // Top Right: Date/Time
      context.textAlign = 'right';
      context.drawImage(icons.dateTime, canvasWidth - 50, videoHeight + 5);
      context.fillText(`${dateTime}`, canvasWidth - 55, videoHeight + 20);

      // Bottom Left: Location
      context.textAlign = 'left';
      context.drawImage(icons.location, 20, videoHeight + 30);
      context.fillText(`${locationInfo}`, 50, videoHeight + 44);

      // Bottom Right: Lat/Long
      context.textAlign = 'right';
      context.drawImage(icons.latLong, canvasWidth - 50, videoHeight + 30);
      context.fillText(`${latLong}`, canvasWidth - 55, videoHeight + 42);

      // Continue the loop
      requestAnimationFrame(drawLiveFeed);
    };

    video.addEventListener('play', drawLiveFeed);

    // Add event listener for window close
    const handleBeforeUnload = () => {
      stopStream();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);

    // Cleanup on unmount
    return () => {
      stopStream();
      window.removeEventListener('beforeunload', handleBeforeUnload);
      if (video.srcObject) {
        (video.srcObject as MediaStream).getTracks().forEach(track => track.stop());
      }
    };
  }, [userName, inspectionId]);

  // Capture snapshot
  const captureSnapshot = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dataURL = canvas.toDataURL('image/png');

    // Convert dataURL to File
    const byteString = atob(dataURL.split(',')[1]);
    const mimeString = dataURL.split(',')[0].split(':')[1].split(';')[0];
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++) {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], { type: mimeString });
    const file = new File([blob], `inspection_${inspectionId}.png`, { type: mimeString });

    setSnapshot(dataURL);
    setFile(file);
    setUploadStatus('');
  };

  // Upload file to backend
  const uploadFile = async () => {
    if (!file) {
      setUploadStatus('No image to upload.');
      return;
    }

    if (!requestId || !establishmentId) {
      setUploadStatus('Request ID and Establishment ID are required.');
      return;
    }

    setIsUploading(true); // Set loading state to true

    const formData = new FormData();
    formData.append('files', file);
    formData.append('requestId', requestId || '');
    formData.append('establishmentId', establishmentId || '');
    formData.append('documentCategory', documentCategory);
    formData.append('documentType', documentType);
    formData.append('notes', notes);
    formData.append('ocrRequired', ocrRequired);

    try {
      const response = await fetch(`${BASE_URL}${backendUrl}`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${auth?.user?.access_token}`,
        },
        body: formData
      });
      if (response.ok) {
        const responseData = await response.json();
        console.log('Upload response:', responseData);
        setUploadStatus('Upload successful!');
        onUploadSuccess(responseData);
      } else {
        throw new Error('Upload failed');
      }
    } catch (error) {
      console.error('Error uploading file:', error);
      setUploadStatus('Upload failed: ' + (error as Error).message);
      onUploadError(error as Error);
    } finally {
      setIsUploading(false); // Set loading state to false regardless of outcome
    }
  };

  // Helper text based on document type
  const getHelperText = () => {
    if (documentType === 'emirates_id') {
      return "Position Emirates ID card";
    } else if (documentType === 'passport') {
      return "Position Passport";
    }
    return "";
  };

  return (
    <div className="px-14 py-0 w-full overflow-hidden">
      <video ref={videoRef} className="hidden"></video>
      <div className="relative">
        <canvas
          ref={canvasRef}
          width={canvasWidth}
          height={canvasHeight}
          className="border rounded mb-4 w-full max-h-[80vh] object-contain"
          style={{ aspectRatio: aspectRatio }}
        ></canvas>

        {/* Camera Switch Button - Only show if multiple cameras available */}
        {cameras.length > 1 && (
          <button
            onClick={() => {
              const nextCameraIndex = (currentCameraIndex + 1) % cameras.length;
              setCurrentCameraIndex(nextCameraIndex);
              startCamera(cameras[nextCameraIndex].deviceId);
            }}
            className="absolute top-2 right-2 bg-[#b89256] text-white p-2 rounded-full shadow-lg hover:bg-[#a17d45] z-10"
            title="Switch Camera"
          >
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"></path>
              <circle cx="12" cy="13" r="4"></circle>
              <path d="M17 21l-5-5 5-5"></path>
            </svg>
          </button>
        )}

        {/* Loading Overlay */}
        {isUploading && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white p-4 rounded-lg shadow-lg text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary mx-auto mb-2"></div>
              <p className="text-sm font-medium">Uploading...</p>
            </div>
          </div>
        )}

        {/* ID Card Mask Overlay */}
        {(documentType === 'emirates_id' || documentType === 'passport') && (
          <div
            className="absolute inset-0 pointer-events-none"
          >
            <div
              className={`absolute ${documentType === 'passport' ? 'passport-mask' : 'id-card-mask'}`}
            ></div>

            {/* Helper Text */}
            <div className="absolute bottom-16 left-0 right-0 text-center">
              <p className="text-white text-lg font-bold bg-black bg-opacity-50 py-2">
                {getHelperText()}
              </p>
            </div>
          </div>
        )}
      <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-4 mb-4 justify-center">
        <button
          onClick={captureSnapshot}
          className="bg-[#b89256] text-white px-4 py-2 rounded w-full sm:w-auto"
        >
          Capture Snapshot
        </button>
        {snapshot && (
          <button
            onClick={uploadFile}
            className="border border-[#b89256] text-white px-4 py-2 rounded w-full sm:w-auto"
          >
            Upload Image
          </button>
        )}
      </div>
      {snapshot && (
        <div className="mb-4">
          <h2 className="text-base md:text-lg font-semibold text-center">Captured Image:</h2>
          <img
            src={snapshot}
            alt="Captured Snapshot"
            className="max-w-full max-h-[50vh] rounded border mx-auto object-contain"
            style={{ aspectRatio: aspectRatio }}
          />
        </div>
      )}
      {uploadStatus && (
        <p className={`text-center text-sm md:text-base ${uploadStatus.includes('failed') ? 'text-red-600' : 'text-green-600'}`}>
          {uploadStatus}
        </p>
      )}
      </div>
      </div>
  );
};

export default InspectionCamera;
